import { z } from 'zod';

export const MATERIAL_KINDS = ['traditional', 'printed'] as const;

export type MaterialKind = (typeof MATERIAL_KINDS)[number];

export const materialKindSchema = z.enum(MATERIAL_KINDS);

export const PART_UNITS = ['mm', 'cm', 'inch', 'meter', 'feet'] as const;

export type PartUnits = (typeof PART_UNITS)[number];

export const partUnitsSchema = z.enum(PART_UNITS);

export const PART_QUANTITY_MAX = 2_147_483_647;

export const CASTOR_MATERIAL_TYPES = ['metal', 'plastic'] as const;

export type CastorMaterialType = (typeof CASTOR_MATERIAL_TYPES)[number];

export const castorMaterialTypeSchema = z.enum(CASTOR_MATERIAL_TYPES);

const castorIdSchema = z.number().int().positive();

const castorNameSchema = z.string().min(1);

export const castorMaterialListSchema = z
  .object({
    traditional: z
      .array(
        z
          .object({
            id: castorIdSchema,
            name: castorNameSchema,
            type: castorMaterialTypeSchema,
            category: castorNameSchema,
          })
          .strict(),
      )
      .min(1),
    categories: z
      .array(
        z
          .object({
            id: castorIdSchema,
            name: castorNameSchema,
            type: castorMaterialTypeSchema,
            defaultMaterialSelectionId: castorIdSchema.nullable(),
          })
          .strict(),
      )
      .min(1),
    printed: z
      .array(
        z
          .object({
            type: castorMaterialTypeSchema,
            category: castorNameSchema,
            subcategory: castorNameSchema.max(100),
            defaultMaterialId: castorNameSchema.max(100),
            materials: z
              .array(
                z
                  .object({
                    id: castorNameSchema.max(100),
                    name: castorNameSchema,
                    printer: castorNameSchema.nullable(),
                  })
                  .strict(),
              )
              .min(1),
          })
          .strict(),
      )
      .min(1),
    printedToTraditional: z.array(
      z
        .object({
          amCategory: castorNameSchema,
          tmMaterialId: castorIdSchema,
          type: z.enum(['category', 'subCategory']),
        })
        .strict(),
    ),
    printedToTraditionalFallback: z.record(castorMaterialTypeSchema, castorIdSchema),
    defaults: z
      .object({
        materialType: castorMaterialTypeSchema,
        traditional: z.record(castorMaterialTypeSchema, castorIdSchema),
        printed: z.record(
          castorMaterialTypeSchema,
          z.object({ category: castorNameSchema, subcategory: castorNameSchema }).strict(),
        ),
      })
      .strict(),
  })
  .strict()
  .superRefine((list, ctx) => {
    const traditionalIds = new Set(list.traditional.map((material) => material.id));
    const printedIds = list.printed.flatMap((family) => family.materials.map(({ id }) => id));
    if (traditionalIds.size !== list.traditional.length) {
      ctx.addIssue({ code: 'custom', path: ['traditional'], message: 'duplicate material id' });
    }
    if (new Set(printedIds).size !== printedIds.length) {
      ctx.addIssue({ code: 'custom', path: ['printed'], message: 'duplicate material id' });
    }
    const referencedIds = [
      ...list.categories.flatMap(({ defaultMaterialSelectionId }) =>
        defaultMaterialSelectionId == null ? [] : [defaultMaterialSelectionId],
      ),
      ...list.printedToTraditional.map(({ tmMaterialId }) => tmMaterialId),
      ...Object.values(list.printedToTraditionalFallback),
      ...Object.values(list.defaults.traditional),
    ];
    for (const id of referencedIds.filter((referenced) => !traditionalIds.has(referenced))) {
      ctx.addIssue({ code: 'custom', message: `material ${id} is not in the traditional list` });
    }
    list.printed.forEach(({ defaultMaterialId, materials }, index) => {
      if (!materials.some(({ id }) => id === defaultMaterialId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['printed', index, 'defaultMaterialId'],
          message: `material ${defaultMaterialId} is not in its family`,
        });
      }
    });
    for (const type of CASTOR_MATERIAL_TYPES) {
      const { category, subcategory } = list.defaults.printed[type];
      if (!findPrintedFamily(list, { type, category, subcategory })) {
        ctx.addIssue({
          code: 'custom',
          path: ['defaults', 'printed', type],
          message: `${category} / ${subcategory} is not a printed ${type} family`,
        });
      }
    }
  });

export type CastorMaterialList = z.infer<typeof castorMaterialListSchema>;

export type CastorTraditionalMaterial = CastorMaterialList['traditional'][number];

export type CastorPrintedFamily = CastorMaterialList['printed'][number];

export type CastorPrintedFamilyKey = Pick<CastorPrintedFamily, 'type' | 'category' | 'subcategory'>;

export type CastorPrintedMaterial = CastorPrintedFamily['materials'][number];

/**
 * Names a 3D-printed material as Castor does, with its printer when it has one.
 * @param material the printed material
 * @returns the material's display name, e.g. "ABS (Ultimaker S5)"
 */
export function printedMaterialLabel({ name, printer }: CastorPrintedMaterial): string {
  return printer ? `${name} (${printer})` : name;
}

/**
 * Finds a 3D-printed material family by its type, category and subcategory.
 * @param list the Castor material list
 * @param key the family's type, category and subcategory
 * @returns the family, or undefined when the list has none
 */
export function findPrintedFamily(
  list: Pick<CastorMaterialList, 'printed'>,
  key: CastorPrintedFamilyKey,
): CastorPrintedFamily | undefined {
  return list.printed.find(
    (family) =>
      family.type === key.type &&
      family.category === key.category &&
      family.subcategory === key.subcategory,
  );
}

/**
 * Castor's traditional material for a 3D-printed family: metals map by category,
 * plastics by subcategory, and anything unmapped takes its type's fallback.
 * @param list the Castor material list
 * @param family the printed family's type, category and subcategory
 * @returns the id of the traditional material the part is analysed against
 */
export function printedTraditionalMaterialId(
  list: Pick<CastorMaterialList, 'printedToTraditional' | 'printedToTraditionalFallback'>,
  family: CastorPrintedFamilyKey,
): number {
  const [mappingType, amCategory] =
    family.type === 'metal'
      ? (['category', family.category] as const)
      : (['subCategory', family.subcategory] as const);
  const mapping = list.printedToTraditional.find(
    (row) => row.type === mappingType && row.amCategory === amCategory,
  );
  return mapping?.tmMaterialId ?? list.printedToTraditionalFallback[family.type];
}

export const ANALYSIS_SOURCES = ['arcessio', 'castor'] as const;

export type AnalysisSource = (typeof ANALYSIS_SOURCES)[number];

export const analysisSourceSchema = z.enum(ANALYSIS_SOURCES);

export const CASTOR_PROJECT_STATUSES = [
  'pending',
  'created',
  'analysing',
  'complete',
  'reanalysing',
  'assembly',
  'failed',
  'deleted',
] as const;

export type CastorProjectStatus = (typeof CASTOR_PROJECT_STATUSES)[number];

export const castorProjectStatusSchema = z.enum(CASTOR_PROJECT_STATUSES);

export const CASTOR_STATUS_PART_IDS_MAX = 100;

export type CastorPartStatus = {
  partId: string;
  status: CastorProjectStatus;
  startedAt: string;
};

export type CastorSession = {
  url: string;
  sessionId: string;
  expiresAt: string;
};
